import { useState } from "react";
import { adminApi } from "@/lib/adminApi";

/**
 * Hook regroupant la logique métier de la carte Artwork :
 * - validate (avec gestion 409 Permanent → confirm force)
 * - reject (avec confirmation)
 * - fetchSuggestions (4 sources étendues, lazy au 1er clic "Autres")
 *
 * Découplé de l'UI pour faciliter les tests unitaires.
 */
export function useArtworkActions({ artist, title, hasTrack, onChanged, initialSuggestions }) {
  const [busy, setBusy] = useState(false);
  const [richSuggestions, setRichSuggestions] = useState(null);
  const [loadingAlt, setLoadingAlt] = useState(false);

  const suggestions = richSuggestions ?? initialSuggestions;

  const validate = async (artworkUrl, source, permanent = false, force = false) => {
    if (!hasTrack || busy || !artworkUrl) return;
    setBusy(true);
    try {
      await adminApi.validate({ artist, title, artwork_url: artworkUrl, source, permanent, force });
      onChanged?.();
    } catch (e) {
      if (e.status === 409) {
        const ok = window.confirm("Cet artwork est marqué Permanent. Voulez-vous vraiment l'écraser ?");
        if (ok) await validate(artworkUrl, source, permanent, true);
      } else {
        alert(e.detail || e.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!hasTrack || busy) return;
    if (!window.confirm("Supprimer la validation actuelle pour ce titre ?")) return;
    setBusy(true);
    try {
      await adminApi.reject(artist, title);
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  const fetchSuggestions = async () => {
    if (!hasTrack || richSuggestions !== null) return;
    setLoadingAlt(true);
    try {
      const r = await adminApi.suggestions(artist, title);
      setRichSuggestions(r.items || []);
    } catch {
      setRichSuggestions(initialSuggestions);
    } finally {
      setLoadingAlt(false);
    }
  };

  return { busy, loadingAlt, suggestions, validate, reject, fetchSuggestions };
}
